/**
 * JP Gems & Jewels - Diamond Purchase (persisted, shared across devices).
 * -----------------------------------------------------------------------------
 * Mirror of the Sales Order flow, but for BUYING diamonds from suppliers:
 *
 *   Supplier  ─►  Purchase Order (JP-DP-0001)  ─►  Purchase Lines
 *
 * Purchase rate auto-fills from the SHARED price book (the "Purchase" price =
 * Rate + 2% from the founder's price chart). Purchase amount = rate x weight.
 * Saved to the backend database via sharedStorage under "hariom-erp-diamondpurchase".
 * Classification pick-lists (shape/quality/sieve/…) are shared with Sales Order.
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { sharedStorage } from './sharedStorage'

export interface PurchaseLine {
  id: string
  date: string
  dueDate: string
  shape: string
  quality: string
  category: string
  sieve: string
  size: string
  code: string
  pcs: number
  weight: number
  purchaseRate: number   // per-carat purchase rate (auto from price book "p")
  remarks: string
  highlight?: boolean    // flag a line to clarify with the customer (shown on downloads)
}

export interface DiamondPO {
  id: string
  poNo: string           // JP-DP-0001 (editable)
  supplier: string
  status: 'open' | 'closed'
  createdDate: string
  closedDate: string
  lines: PurchaseLine[]
}

export function purchaseTotal(po: DiamondPO) {
  return po.lines.reduce((t, l) => t + l.purchaseRate * l.weight, 0)
}

const today = () => new Date().toISOString().slice(0, 10)

interface DiamondPurchaseState {
  orders: DiamondPO[]
  suppliers: string[]
  poSeq: number

  addSupplier: (s: string) => void
  deleteSupplier: (s: string) => void                   // founder-only: remove a supplier + all its POs
  ensureOpenPO: (supplier: string) => string
  setPoNo: (id: string, no: string) => void
  closePO: (id: string) => void
  removePO: (id: string) => void
  addLine: (id: string, l: PurchaseLine) => void
  updateLine: (id: string, lineId: string, l: PurchaseLine) => void
  removeLine: (id: string, lineId: string) => void
}

export const useDiamondPurchaseStore = create<DiamondPurchaseState>()(
  persist(
    (set, get) => ({
      orders: [],
      suppliers: [],
      poSeq: 0,

      addSupplier: (v) => set((s) => (s.suppliers.includes(v) ? {} : { suppliers: [...s.suppliers, v] })),
      deleteSupplier: (v) => set((s) => ({ suppliers: s.suppliers.filter((x) => x !== v), orders: s.orders.filter((o) => o.supplier !== v) })),
      ensureOpenPO: (supplier) => {
        const s = get()
        const open = s.orders.find((o) => o.supplier === supplier && o.status === 'open')
        if (open) return open.id
        const seq = s.poSeq + 1
        const po: DiamondPO = {
          id: `dp-${Date.now()}`, poNo: `JP-DP-${String(seq).padStart(4, '0')}`,
          supplier, status: 'open', createdDate: today(), closedDate: '', lines: [],
        }
        set({ orders: [...s.orders, po], poSeq: seq, suppliers: s.suppliers.includes(supplier) ? s.suppliers : [...s.suppliers, supplier] })
        return po.id
      },
      setPoNo: (id, no) => set((s) => ({ orders: s.orders.map((o) => (o.id === id ? { ...o, poNo: no } : o)) })),
      closePO: (id) => set((s) => ({ orders: s.orders.map((o) => (o.id === id ? { ...o, status: 'closed', closedDate: today() } : o)) })),
      removePO: (id) => set((s) => ({ orders: s.orders.filter((o) => o.id !== id) })),
      addLine: (id, l) => set((s) => ({ orders: s.orders.map((o) => (o.id === id ? { ...o, lines: [...o.lines, l] } : o)) })),
      updateLine: (id, lineId, l) => set((s) => ({ orders: s.orders.map((o) => (o.id === id ? { ...o, lines: o.lines.map((x) => (x.id === lineId ? l : x)) } : o)) })),
      removeLine: (id, lineId) => set((s) => ({ orders: s.orders.map((o) => (o.id === id ? { ...o, lines: o.lines.filter((x) => x.id !== lineId) } : o)) })),
    }),
    {
      name: 'hariom-erp-diamondpurchase',
      storage: createJSONStorage(() => sharedStorage),
      partialize: (s) => ({ orders: s.orders, suppliers: s.suppliers, poSeq: s.poSeq }),
    }
  )
)
